import { isDefined } from "@clipboard-health/util-ts";

import { type PatientData, type VisitWithCaseData } from "../types";
import { type VisitFilters } from "./types";

interface FilterVisitsProps {
  visits: VisitWithCaseData[];
  caseIdToSpecialtiesMap: Map<string, string[]>;
  caseIdToPatientMap: Map<string, PatientData>;
  filters: Partial<VisitFilters>;
}

function shouldInclude(filter: string[] | undefined, value: string | undefined): boolean {
  if (!isDefined(filter) || filter.length === 0) {
    return true;
  }

  return isDefined(value) && filter.includes(value);
}

export function getPerVisitPay(visit: VisitWithCaseData): number | undefined {
  const { payRate, pricingType, estimatedDuration } = visit.attributes;
  if (!isDefined(payRate)) {
    return undefined;
  }

  if (pricingType === "PER_HOUR") {
    if (!isDefined(estimatedDuration)) {
      return undefined;
    }
    return Math.round(payRate.amountInMinorUnits * estimatedDuration);
  }

  return payRate.amountInMinorUnits;
}

export function filterVisits(props: FilterVisitsProps): VisitWithCaseData[] {
  const { visits, caseIdToSpecialtiesMap, caseIdToPatientMap, filters } = props;
  const { shiftRoles, excludedCareTypes, visitTypes, payMinimum, visitsPerWeekMaximum, distance } =
    filters;

  return visits.filter((visit) => {
    if (isDefined(distance)) {
      const patient = caseIdToPatientMap.get(visit.attributes.caseId);
      if (patient && patient.attributes.distanceToWorkerInMiles > distance) {
        return false;
      }
    }

    if (!shouldInclude(shiftRoles, visit.attributes.workerReq ?? undefined)) {
      return false;
    }

    if (!shouldInclude(visitTypes, visit.attributes.type)) {
      return false;
    }

    if (isDefined(excludedCareTypes) && excludedCareTypes.length > 0) {
      const specialties = caseIdToSpecialtiesMap.get(visit.attributes.caseId) ?? [];
      const hasNonExcludedSpecialty = specialties.some(
        (specialty) => !excludedCareTypes.includes(specialty)
      );
      if (specialties.length > 0 && !hasNonExcludedSpecialty) {
        return false;
      }
    }

    if (isDefined(visitsPerWeekMaximum)) {
      const vpw = visit.attributes.visitsPerWeek;
      if (!isDefined(vpw) || vpw > visitsPerWeekMaximum) {
        return false;
      }
    }

    if (isDefined(payMinimum)) {
      const perVisitPay = getPerVisitPay(visit);
      if (!isDefined(perVisitPay) || perVisitPay < payMinimum) {
        return false;
      }
    }

    return true;
  });
}
